import { motion } from "framer-motion";
import { FileText, ArrowUpRight, Star, Check } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import { PRODUCTS } from "@/data/content";
import { Reveal } from "./Reveal";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const Cover = ({ p, className = "" }) =>
  p.images ? (
    <div className={`relative bg-gradient-to-br ${p.tone} ${className}`}>
      <img src={p.images[0]} alt="" className="absolute left-3 top-4 bottom-4 w-[55%] object-cover rounded-lg shadow-lg -rotate-6" />
      <img src={p.images[1]} alt="" className="absolute right-3 top-4 bottom-4 w-[55%] object-cover rounded-lg shadow-xl rotate-6" />
    </div>
  ) : p.image ? (
    <img src={p.image} alt={`Okładka: ${p.title}`} className={`object-cover object-top ${className}`} />
  ) : (
    <div className={`grid place-items-center bg-gradient-to-br ${p.tone} ${className}`}>
      <FileText size={36} className="text-violet-deep/70" strokeWidth={1.4} />
    </div>
  );

const buy = (p) => {
  window.open(p.link, "_blank", "noopener,noreferrer");
  toast.success("Przenoszę do sklepu…", { description: p.title });
};

export const Products = () => (
  <section id="produkty" data-testid={TESTIDS.products.section} className="py-24 lg:py-32 px-5 sm:px-8">
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <p className="text-sm tracking-widest uppercase text-violet-deep font-600">Sklep</p>
        <h2 className="mt-4 font-display font-700 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ink max-w-2xl">
          Wybierz swój e-book
        </h2>
      </Reveal>

      <div className="mt-16 flex flex-wrap justify-center gap-6">
        {PRODUCTS.map((p, i) => (
          <Reveal key={i} delay={i * 0.08} className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)]">
            <motion.div
              data-testid={TESTIDS.products.card(i)}
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className={`group relative h-full flex flex-col rounded-[1.75rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)] ${
                p.featured ? "bg-ink text-white ring-2 ring-violet" : "bg-white text-ink border border-ink/5"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-violet px-3 py-1 text-xs font-600 text-white shadow-lg">
                  <Star size={12} className="fill-white" /> {p.badge}
                </span>
              )}
              {p.isNew && (
                <span className="absolute -top-3 right-6 z-10 inline-flex items-center rounded-full bg-emerald-500 px-3 py-1 text-xs font-700 uppercase tracking-wide text-white shadow-lg">
                  Nowość
                </span>
              )}
              <Cover p={p} className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl mb-5" />

              <span className={`text-xs font-600 uppercase tracking-wider ${p.featured ? "text-violet-soft" : "text-violet-deep"}`}>
                {p.type}
              </span>
              <h3 className="mt-2 font-display font-600 text-lg leading-snug">{p.title}</h3>
              <p className={`mt-2 text-sm leading-relaxed flex-1 ${p.featured ? "text-white/60" : "text-ink/55"}`}>
                {p.desc}
              </p>

              <Dialog>
                <DialogTrigger asChild>
                  <button
                    data-testid={TESTIDS.products.details(i)}
                    className={`mt-3 self-start text-sm font-600 underline underline-offset-4 transition-colors ${
                      p.featured ? "text-violet-soft hover:text-white" : "text-violet-deep hover:text-ink"
                    }`}
                  >
                    Czytaj więcej
                  </button>
                </DialogTrigger>
                <DialogContent data-testid={TESTIDS.products.dialog(i)} className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
                  <div className="grid sm:grid-cols-[160px_1fr] gap-6">
                    <Cover p={p} className="relative hidden sm:block aspect-[3/4] w-full overflow-hidden rounded-xl" />
                    <div>
                      <DialogHeader>
                        <span className="text-xs font-600 uppercase tracking-wider text-violet-deep">{p.type}</span>
                        <DialogTitle className="font-display font-700 text-2xl tracking-tight text-ink text-left">
                          {p.title}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="mt-4 space-y-3 text-ink/65 leading-relaxed text-sm">
                        {p.long.map((para, k) => <p key={k}>{para}</p>)}
                        {p.bullets && (
                          <div>
                            {p.bulletsTitle && <p className="font-600 text-ink">{p.bulletsTitle}</p>}
                            <ul className="mt-2 space-y-1.5">
                              {p.bullets.map((b, k) => (
                                <li key={k} className="flex gap-2">
                                  <Check size={16} className="mt-0.5 shrink-0 text-violet-deep" /> <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {p.outro && <p>{p.outro}</p>}
                      </div>
                      {p.toc && (
                        <div className="mt-5">
                          <p className="text-sm font-600 text-ink">Spis treści (podgląd):</p>
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {p.toc.map((src, k) => (
                              <img
                                key={k}
                                src={src}
                                alt={`Spis treści, strona ${k + 1}`}
                                className="w-full rounded-md ring-1 ring-black/5 object-cover"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-6 flex items-center justify-between gap-4">
                        <span className="font-display font-700 text-3xl text-ink">{p.price}</span>
                        <button
                          data-testid={TESTIDS.products.dialogBuy(i)}
                          onClick={() => buy(p)}
                          className="inline-flex items-center gap-2 rounded-full bg-violet px-6 py-3 text-sm font-500 text-white hover:bg-violet-deep transition-colors duration-300"
                        >
                          Kup teraz <ArrowUpRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="mt-5">
                <span className="font-display font-700 text-2xl">{p.price}</span>
              </div>
              <button
                data-testid={TESTIDS.products.buy(i)}
                onClick={() => buy(p)}
                className={`mt-4 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-500 transition-colors duration-300 ${
                  p.featured ? "bg-violet text-white hover:bg-violet-deep" : "bg-ink text-white hover:bg-violet-deep"
                }`}
              >
                Kup teraz <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
              </button>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
